/**
 * Shared types for the dashboard page tree.
 *
 * `PinnedContext` is computed once at the top of `DashboardPage` from
 * `snapshot.pinned_to_meeting` and passed down to every section that
 * renders an interactive control. Sections use it to:
 *   - disable mutations (Submit toggle, Goal cell, IA/PI edit, etc.)
 *   - show a tooltip explaining why
 *   - render the historical-view banner
 *
 * The decision rule for disabling (locked per V1.1 plan §2, Q3 option i):
 *   when `pinned` is non-null, disable every control that mutates the
 *   pinned meeting's state. The "Schedule next review" footer button is
 *   the one exception — it creates a *future* meeting and is unaffected.
 */
import type { MeetingDetail } from "@/api/models";

export interface PinnedContext {
  /** Display label for the pinned meeting (e.g. "Q2 Review"). */
  meetingLabel: string;
  /** ISO datetime of the meeting's as-of time. Used by the trajectory marker. */
  pinnedAsOf: string | null;
  /** Performance year of the pinned meeting (for the tooltip copy). */
  year: number;
}

/**
 * Build a `PinnedContext` from a meeting + the pinned `pinned_as_of`.
 * Returns null when the meeting is missing — caller already checked
 * `snapshot.pinned_to_meeting` and we still want a graceful fallback.
 */
export function buildPinnedContext(
  meeting: MeetingDetail | null,
  pinnedAsOf: string | null | undefined,
  fallbackYear: number,
): PinnedContext | null {
  if (!meeting) return null;
  const label = meeting.label && meeting.label.trim() !== ""
    ? meeting.label
    : `Q${meeting.quarter ?? "?"} Review`;
  return {
    meetingLabel: label,
    pinnedAsOf: pinnedAsOf ?? null,
    // MeetingDetail doesn't carry performance_year directly in v0.3.0/v0.3.1 —
    // fall back to the year computed from the dashboard URL.
    year: fallbackYear,
  };
}

/**
 * Tooltip text shown on disabled controls in pinned mode.
 *
 * Per the V1.1 plan §2 Q3 option (i): "Pinned to Q[N] [YYYY] · use
 * 'Back to current' to edit".
 */
export function pinnedTooltip(p: PinnedContext): string {
  return `Pinned to ${p.meetingLabel} ${p.year} · use "Back to current" to edit`;
}
